import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Avatar,
  Button
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import FormatAmount from './FormatAmount';
import FormatCryptoAmount from './FormatCryptoAmount';
import AccountMappingModal from './AccountMappingModal';

const SourceAccounts = ({ selectedSource, sourceAccounts, isLoading, error, onAccountClick, currentCompany, csrfToken }) => {
  const [mappingModalOpen, setMappingModalOpen] = useState(false);
  
  // Check if any accounts don't have Xero mapping
  const hasUnmappedAccounts = sourceAccounts && sourceAccounts.some(account => !account.has_dependency);
  // Helper function to get wallet logo based on source_name
  const getWalletLogo = (sourceName) => {
    const walletMap = {
      'coinbase_pro': '/exchanges/coinbase_pro.png',
      'coinbase': '/exchanges/coinbase.png',
      'binance': '/exchanges/binance.png',
      'metamask': '/exchanges/metamask.png',
      'bitcoin': '/exchanges/bitcoin.png',
      'ethereum': '/exchanges/ethereum.png'
    };
    return walletMap[sourceName] || '/exchanges/default.png';
  };

  // Helper function to get token name from account name
  const getTokenName = (accountName) => {
    // Extract token name from account name (e.g., "Coinbase PRO - GBP" -> "GBP")
    const parts = accountName.split(' - ');
    return parts[parts.length - 1] || accountName;
  };

  // Helper function to check if account is cryptocurrency
  const isCryptoAccount = (accountName) => {
    const tokenName = getTokenName(accountName);
    const cryptoTokens = ['BTC', 'ETH', 'MATIC', 'SOL', 'USDC', 'USDT'];
    return cryptoTokens.includes(tokenName.toUpperCase());
  };

  // Helper function to get token icon
  const getTokenIcon = (tokenName) => {
    return `/tokens/${tokenName.toUpperCase()}.png`;
  };

  // Helper function to get token icon with fallback
  const getTokenIconWithFallback = (tokenName) => {
    const iconPath = getTokenIcon(tokenName);
    return iconPath;
  };

  // Helper function to get currency symbol
  const getCurrencySymbol = (tokenName) => {
    switch (tokenName?.toUpperCase()) {
      case 'GBP': return '£';
      case 'USD': return '$';
      case 'EUR': return '€';
      default: return tokenName;
    }
  };

  // Get sync status based on real data from backend
  const getSyncStatus = (account) => {
    // If there are unreconciled entries, show reconciliation status instead of sync status
    if (account.unreconciled_count > 0) {
      return {
        text: `${account.unreconciled_count} UNRECONCILED`,
        icon: <SyncIcon sx={{ fontSize: 16 }} />
      };
    }
    
    // If there are pending sync entries, show sync status
    if (account.has_pending_sync && account.unsynced_count > 0) {
      return {
        text: `${account.unsynced_count} PENDING SYNC`,
        icon: <SyncIcon sx={{ fontSize: 16 }} />
      };
    }
    
    // Only show SYNCED when everything is reconciled and synced
    return {
      text: 'SYNCED',
      icon: <CheckCircleIcon sx={{ fontSize: 16, color: 'green' }} />
    };
  };



  if (isLoading) {
    return (
      <Box sx={{ pb: 4 }}>
        {/* Skeleton Cards */}
        {[1, 2, 3, 4].map((index) => (
          <Card key={index} sx={{ 
            mb: 1, 
            borderRadius: 2, 
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
          }}>
            <CardContent sx={{ p: 2 }}>
              {/* Header Section Skeleton */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                     {/* Token Icon Skeleton */}
                   <Box sx={{ 
                     width: 32, 
                     height: 32, 
                     borderRadius: 2, 
                     mr: 1.5, 
                     bgcolor: '#f3f4f6',
                     animation: 'pulse 1.5s ease-in-out infinite'
                   }} />
                  
                  {/* Account Name Skeleton */}
                  <Box sx={{ 
                    width: 120, 
                    height: 20, 
                    bgcolor: '#f3f4f6',
                    borderRadius: 1,
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }} />
                </Box>

                {/* Status Chip Skeleton */}
                <Box sx={{ 
                  width: 140, 
                  height: 22, 
                  bgcolor: '#f3f4f6',
                  borderRadius: 1,
                  animation: 'pulse 1.5s ease-in-out infinite'
                }} />
              </Box>

              {/* Reconciled Status Skeleton */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ 
                  width: 18, 
                  height: 18, 
                  borderRadius: '50%', 
                  mr: 0.75, 
                  bgcolor: '#f3f4f6',
                  animation: 'pulse 1.5s ease-in-out infinite'
                }} />
                <Box sx={{ 
                  width: 80, 
                  height: 16, 
                  bgcolor: '#f3f4f6',
                  borderRadius: 1,
                  animation: 'pulse 1.5s ease-in-out infinite'
                }} />
              </Box>

              {/* Balance Details Skeleton */}
              <Box sx={{ mt: 1 }}>
                {/* Row 1 */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', py: 0.5, borderTop: '1px solid #e5e7eb' }}>
                  <Box sx={{ 
                    width: 100, 
                    height: 14, 
                    bgcolor: '#f3f4f6',
                    borderRadius: 1,
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }} />
                  <Box sx={{ 
                    width: 80, 
                    height: 14, 
                    bgcolor: '#f3f4f6',
                    borderRadius: 1,
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }} />
                </Box>

                {/* Row 2 */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', py: 0.5, borderTop: '1px solid #e5e7eb' }}>
                  <Box sx={{ 
                    width: 120, 
                    height: 14, 
                    bgcolor: '#f3f4f6',
                    borderRadius: 1,
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }} />
                  <Box sx={{ 
                    width: 80, 
                    height: 14, 
                    bgcolor: '#f3f4f6',
                    borderRadius: 1,
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }} />
                </Box>


              </Box>
            </CardContent>
          </Card>
        ))}
        
        {/* CSS Animation */}
        <style>
          {`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}
        </style>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="body2" color="error">{error}</Typography>
      </Box>
    );
  }

  const visibleAccounts = (sourceAccounts || []).filter(a => a.external_id && String(a.external_id).length > 0);

  if (!selectedSource || visibleAccounts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No accounts found for {selectedSource?.display_name || 'selected source'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Accounts will appear here once they are synced from the source.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header with mapping button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {selectedSource?.display_name} Accounts
        </Typography>
        <Button
          variant={hasUnmappedAccounts ? "contained" : "outlined"}
          color={hasUnmappedAccounts ? "warning" : "primary"}
          size="small"
          startIcon={<SettingsIcon />}
          onClick={() => setMappingModalOpen(true)}
        >
          {hasUnmappedAccounts ? `Map to Xero (${sourceAccounts?.filter(acc => !acc.has_dependency).length || 0} unmapped)` : 'Map to Xero'}
        </Button>
      </Box>
      
      {/* Warning message for unmapped accounts */}
      {hasUnmappedAccounts && (
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          bgcolor: '#fef3c7', 
          border: '1px solid #f59e0b', 
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <SettingsIcon sx={{ color: '#f59e0b' }} />
          <Typography variant="body2" sx={{ color: '#92400e' }}>
            Some accounts need Xero mapping. Click on any account card or the "Map to Xero" button to configure mappings.
          </Typography>
        </Box>
      )}
      
      {/* Accounts List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {visibleAccounts.map((account) => {
          const tokenName = getTokenName(account.name);
          const isCrypto = isCryptoAccount(account.name);
          const syncStatus = getSyncStatus(account);

          return (
            <Box key={account.id}>
              <Card 
                sx={{ 
                  borderRadius: 2,
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    borderColor: '#3b82f6'
                  }
                }}
                onClick={() => {
                  // If there are unmapped accounts, show the mapping modal first
                  if (hasUnmappedAccounts) {
                    setMappingModalOpen(true);
                  } else if (onAccountClick) {
                    onAccountClick(account);
                  } else {
                    // Navigate to account details page
                    window.location.href = `/accounts/${account.id}`;
                  }
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  {/* Header Section */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      {/* Token Icon */}
                      <Box
                        component="img"
                        src={getTokenIconWithFallback(tokenName)}
                        alt={`${tokenName} icon`}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 2,
                          mr: 1.5
                        }}
                        onError={(e) => {
                          // Fallback to wallet logo if token icon fails
                          e.target.src = getWalletLogo(selectedSource.source_name);
                          e.target.onerror = null; // Prevent infinite loop
                        }}
                      />
                      
                      {/* Account Name */}
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1f2937', fontSize: '0.95rem', lineHeight: 1 }}>
                        {account.name}
                      </Typography>
                    </Box>

                    {/* Status and Menu */}
                    <Chip
                      icon={syncStatus.icon}
                      label={syncStatus.text}
                      size="small"
                      sx={{
                        bgcolor: syncStatus.text.includes('SYNCED') ? '#dcfce7' : '#fef3c7',
                        color: syncStatus.text.includes('SYNCED') ? '#166534' : '#92400e',
                        fontWeight: 500,
                        fontSize: '0.72rem',
                        height: 22
                      }}
                    />
                  </Box>

                  {/* Balance Section */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {account.is_reconciled ? (
                        <>
                          <CheckCircleIcon sx={{ color: '#10b981', mr: 0.75, fontSize: 18 }} />
                          <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
                            Reconciled
                          </Typography>
                        </>
                      ) : (
                        <>
                          <SyncIcon sx={{ color: '#f59e0b', mr: 0.75, fontSize: 18 }} />
                          <Typography variant="caption" sx={{ color: '#92400e', fontWeight: 500 }}>
                            Pending Reconciliation
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Balance Details */}
                  <Box sx={{ mt: 1 }}>
                    {/* Row 1 */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', py: 0.5, borderTop: '1px solid #e5e7eb' }}>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        Statement balance
                      </Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        {isCrypto ? (
                          <>
                            <FormatAmount amount={account.statement_balance || 0} currency="GBP" />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              <FormatCryptoAmount amount={account.token_balance || 0} symbol={tokenName} maximumFractionDigits={8} />
                            </Typography>
                          </>
                        ) : (
                          <FormatAmount amount={account.statement_balance || 0} currency={tokenName} />
                        )}
                      </Box>
                    </Box>

                    {/* Row 2 */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', py: 0.5, borderTop: '1px solid #e5e7eb' }}>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        Balance in ContaCrypto
                      </Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        {account.has_dependency ? (
                          isCrypto ? (
                            <>
                              <FormatAmount amount={account.contacrypto_balance || 0} currency="GBP" />
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                <FormatCryptoAmount amount={account.contacrypto_token_balance || 0} symbol={tokenName} maximumFractionDigits={8} />
                              </Typography>
                            </>
                          ) : (
                            <FormatAmount amount={account.contacrypto_balance || 0} currency={tokenName} />
                          )
                        ) : (
                          <Typography variant="caption" sx={{ color: '#ef4444', fontStyle: 'italic' }}>
                            No Xero mapping
                          </Typography>
                        )}
                      </Box>
                    </Box>


                  </Box>
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>
      
      {/* Account Mapping Modal */}
      <AccountMappingModal
        open={mappingModalOpen}
        onClose={() => setMappingModalOpen(false)}
        selectedSource={selectedSource}
        sourceAccounts={sourceAccounts}
        companyId={currentCompany?.id}
        csrfToken={csrfToken}
      />
    </Box>
  );
};

SourceAccounts.propTypes = {
  selectedSource: PropTypes.shape({
    id: PropTypes.number.isRequired,
    display_name: PropTypes.string.isRequired,
    source_name: PropTypes.string.isRequired
  }),
  sourceAccounts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    code: PropTypes.string,
    balance: PropTypes.number,
    dependency_id: PropTypes.number,
    has_dependency: PropTypes.bool
  })).isRequired,
  onAccountClick: PropTypes.func,
  currentCompany: PropTypes.shape({
    id: PropTypes.number.isRequired
  }),
  csrfToken: PropTypes.string.isRequired
};

export default SourceAccounts; 
